import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@up2d8/ui';
import { AdminIcon } from './icons';

/**
 * Paneel rechts (`.a-drawer`, 560 breed) en venster in het midden (`.a-modal`, 540, breed 760),
 * spec A §5 en §6 "Aanmaken": korte vorm → venster, langere vorm → paneel.
 *
 * Beide zijn `role="dialog"` met `aria-modal`, een titel als naam, Escape sluit, de focus gaat bij
 * openen naar het eerste veld (of het venster zelf) en blijft binnen, en gaat bij sluiten terug
 * naar waar ze was. Geen portal: ze moeten binnen `data-zone` staan voor de tokens, en ze staan
 * `position: fixed`.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function useDialog(ref: RefObject<HTMLDivElement | null>, open: boolean, onClose: () => void) {
  // De laatste `onClose` zonder de effecten opnieuw te laten lopen bij elke render.
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const dialog = ref.current;
    // Het eerste veld in de inhoud, niet de sluitknop: wie "Nieuw …" klikt, wil beginnen typen.
    const first =
      dialog?.querySelector<HTMLElement>(
        '.a-drawer-body input, .a-drawer-body select, .a-drawer-body textarea',
      ) ?? dialog;
    first?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close.current();
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;
      const items = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const firstItem = items[0] as HTMLElement;
      const lastItem = items[items.length - 1] as HTMLElement;
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, [open, ref]);
}

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Het onderschrift onder de titel ("Behandeld door Preventie"). */
  sub?: ReactNode;
  /** De knoppen rechts in de voet: Annuleren · hoofdactie. */
  footer?: ReactNode;
  testId?: string;
  children: ReactNode;
}

function DialogHead({
  titleId,
  title,
  sub,
  onClose,
}: {
  titleId: string;
  title: ReactNode;
  sub?: ReactNode;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="a-drawer-head">
      <div style={{ flex: 1, minWidth: 0 }}>
        <h2 className="a-drawer-title" id={titleId}>
          {title}
        </h2>
        {sub ? (
          <div className="a-meta" style={{ marginTop: 2 }}>
            {sub}
          </div>
        ) : null}
      </div>
      <button
        type="button"
        className="a-iconbtn"
        aria-label={t('beheer.kit.close')}
        onClick={onClose}
      >
        <AdminIcon name="x" size={16} />
      </button>
    </div>
  );
}

/** Het paneel rechts: volle hoogte, 560 breed, over een donkere laag. */
export function Drawer({ open, onClose, title, sub, footer, testId, children }: DialogProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialog(ref, open, onClose);
  if (!open) return null;
  return (
    <>
      <div className="a-scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        className="a-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid={testId}
      >
        <DialogHead titleId={titleId} title={title} sub={sub} onClose={onClose} />
        <div className="a-drawer-body">{children}</div>
        {footer ? <div className="a-drawer-foot">{footer}</div> : null}
      </div>
    </>
  );
}

/** Het venster in het midden van het inhoudsgebied: 540 breed, of 760 met `wide`. */
export function Modal({
  open,
  onClose,
  title,
  sub,
  footer,
  wide,
  testId,
  children,
}: DialogProps & { wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialog(ref, open, onClose);
  if (!open) return null;
  return (
    <>
      <div className="a-scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        className={cn('a-modal', wide && 'wide')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid={testId}
      >
        <DialogHead titleId={titleId} title={title} sub={sub} onClose={onClose} />
        <div className="a-drawer-body">{children}</div>
        {footer ? <div className="a-drawer-foot">{footer}</div> : null}
      </div>
    </>
  );
}
